import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { parseLocalStorage } from '../../utils/localStorage';
import apiService from '../../utils/apiService';

// Recharge wallet with specified amount
export const rechargeWallet = createAsyncThunk(
    'walletRecharge/rechargeWallet',
    async ({ botId, amount, ownerWalletAddress, transactionSignature, rechargeType, tokenAddress, middleSweepOnly }, { rejectWithValue }) => {
        try {
            const parsedAmount = Number(amount);
            const body = {
                amount: parsedAmount,
                rechargeType,
            };
            if (ownerWalletAddress) {
                body.ownerWalletAddress = ownerWalletAddress;
            }
            if (transactionSignature) {
                body.transactionSignature = transactionSignature;
            }
            if (tokenAddress != null && tokenAddress !== '') {
                body.tokenAddress = tokenAddress;
            }
            if (middleSweepOnly === true) {
                body.middleSweepOnly = true;
            }

            const response = await apiService.post(`/bots/${botId}/recharge`, body, {
                maxRetries: 0,
                timeout: 600000
            });

            return response.data;
        } catch (error) {
            console.error('Recharge wallet error:', error);
            const validationDetails = error?.data?.details;
            const validationHint =
              Array.isArray(validationDetails) && validationDetails.length > 0
                ? validationDetails.map((d) => d.message).join('; ')
                : null;
            const msg =
              validationHint ||
              (error?.data &&
                (error.data.message ||
                  (typeof error.data.error === "string"
                    ? error.data.error
                    : null))) ||
              error?.message ||
              'Failed to recharge wallet';
            return rejectWithValue(msg);
        }
    }
);

// Mobile/Tablet recharge
export const rechargeWalletMobileTablet = createAsyncThunk(
    'walletRecharge/rechargeWalletMobileTablet',
    async ({ botId, deviceType }, { rejectWithValue }) => {
        try {
            const response = await apiService.post(`/bots/${botId}/recharge-mobile-tablet`, {
                deviceType,
            }, {
                maxRetries: 5, // Allow up to 5 retries for recharge operations
                timeout: 600000 // 10 minutes timeout
            });

            return response.data;
        } catch (error) {
            console.error('recharge error:', error.message);
            const msg =
              (error?.data &&
                (error.data.message ||
                  (typeof error.data.error === "string"
                    ? error.data.error
                    : null))) ||
              error?.message ||
              'Failed to recharge  wallet';
            return rejectWithValue(msg);
        }
    }
);

// Get recharge history
export const getRechargeHistory = createAsyncThunk(
    'walletRecharge/getRechargeHistory',
    async (botId, { rejectWithValue }) => {
        try {
            const response = await apiService.get(`/bots/${botId}/recharge-history`);
            return response.data;
        } catch (error) {
            console.error('Get recharge history error:', error);
            return rejectWithValue(error.message || 'Failed to get recharge history');
        }
    }
);

const initialState = {
    rechargeLoading: false,
    mobileTabletRechargeLoading: false,
    historyLoading: false,
    error: null,
    success: false,
    rechargeHistory: {},
    lastRecharge: null,
    lastError: null // Track last error to prevent duplicates
};

const walletRechargeSlice = createSlice({
    name: 'walletRecharge',
    initialState,
    reducers: {
        resetRechargeState(state) {
            state.rechargeLoading = false;
            state.mobileTabletRechargeLoading = false;
            state.historyLoading = false;
            state.error = null;
            state.success = false;
            state.lastRecharge = null;
            state.lastError = null;
        },
        clearRechargeError(state) {
            state.error = null;
            state.lastError = null;
        },
        clearRechargeSuccess(state) {
            state.success = false;
        }
    },
    extraReducers: (builder) => {
        builder
            // Recharge wallet cases
            .addCase(rechargeWallet.pending, (state) => {
                state.rechargeLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(rechargeWallet.fulfilled, (state, action) => {
                state.rechargeLoading = false;
                state.success = true;
                state.lastRecharge = action.payload;
                state.error = null;
            })
            .addCase(rechargeWallet.rejected, (state, action) => {
                state.rechargeLoading = false;
                const errorMessage = action.payload || action.error.message;
                // Only set error if it's different from the last error
                if (state.lastError !== errorMessage) {
                    state.error = errorMessage;
                    state.lastError = errorMessage;
                }
                state.success = false;
            })
            
            // Mobile/Tablet recharge cases
            .addCase(rechargeWalletMobileTablet.pending, (state) => {
                state.mobileTabletRechargeLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(rechargeWalletMobileTablet.fulfilled, (state, action) => {
                state.mobileTabletRechargeLoading = false;
                state.success = true;
                state.lastRecharge = action.payload;
                state.error = null;
            })
            .addCase(rechargeWalletMobileTablet.rejected, (state, action) => {
                state.mobileTabletRechargeLoading = false;
                const errorMessage = action.payload || action.error.message;
                // Only set error if it's different from the last error
                if (state.lastError !== errorMessage) {
                    state.error = errorMessage;
                    state.lastError = errorMessage;
                }
                state.success = false;
            })
            
            // Get recharge history cases
            .addCase(getRechargeHistory.pending, (state) => {
                state.historyLoading = true;
                state.error = null;
            })
            .addCase(getRechargeHistory.fulfilled, (state, action) => {
                state.historyLoading = false;
                const key = String(action.payload.botId);
                state.rechargeHistory[key] = action.payload.history;
                state.error = null;
            })
            .addCase(getRechargeHistory.rejected, (state, action) => {
                state.historyLoading = false;
                const errorMessage = action.payload || action.error.message;
                // Only set error if it's different from the last error
                if (state.lastError !== errorMessage) {
                    state.error = errorMessage;
                    state.lastError = errorMessage;
                }
            });
    },
});

export const { resetRechargeState, clearRechargeError, clearRechargeSuccess } = walletRechargeSlice.actions;

// Selectors
export const selectRechargeLoading = (state) => state.walletRecharge.rechargeLoading;
export const selectMobileTabletRechargeLoading = (state) => state.walletRecharge.mobileTabletRechargeLoading;
export const selectHistoryLoading = (state) => state.walletRecharge.historyLoading;
export const selectRechargeError = (state) => state.walletRecharge.error;
export const selectRechargeSuccess = (state) => state.walletRecharge.success;
export const selectLastRecharge = (state) => state.walletRecharge.lastRecharge;
export const selectRechargeHistory = (state, botId) => {
    const key = botId != null && botId !== '' ? String(botId) : '';
    return key ? (state.walletRecharge.rechargeHistory[key] || []) : [];
};

export default walletRechargeSlice.reducer; 